import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  AssetPath,
  B2CRouting,
  CLIENT_APP_ID,
  ClientAppId,
  WeatherEvent,
} from '@sc/data-access-models';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ICON_LIST, IconName, IconsMap } from '../types';

@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  private readonly ROOT_PATH = './assets';
  private readonly DEFAULT_FOLDER = 'default';
  private readonly INUSRER_LOGO_PATH = 'insurance_logo';

  private readonly iconsMap = new BehaviorSubject<IconsMap | null>(null);
  private readonly logoAlt = new BehaviorSubject<LogoAlt | null>(null);
  private readonly routingCustomer = new BehaviorSubject<B2CRouting | null>(
    null
  );
  private readonly weatherEvents = new BehaviorSubject<WeatherEvent[] | null>(
    null
  );

  constructor(
    private readonly httpClient: HttpClient,
    private readonly configService: ConfigurationService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    this.httpClient
      .get<IconsMap>(this.getAssetPath('iconsMap.json'), {
        responseType: 'json',
      })
      .pipe(take(1))
      .subscribe((response) => this.iconsMap.next(response));

    this.httpClient
      .get<LogoAlt>(this.getAssetPath('logoAlt.json', true), {
        responseType: 'json',
      })
      .pipe(take(1))
      .subscribe((response) => this.logoAlt.next(response));

    if (this.clientAppId === 'customer') {
      this.httpClient
        .get<B2CRouting>(this.getAssetPath('routing-customer.json'), {
          responseType: 'json',
        })
        .pipe(take(1))
        .subscribe((response) => this.routingCustomer.next(response));
    }

    this.httpClient
      .get<WeatherEvent[]>(
        this.getAssetPath(`weather-events.json?t=${Date.now()}`),
        {
          responseType: 'json',
        }
      )
      .pipe(take(1))
      .subscribe((response) => this.weatherEvents.next(response));

    this.setFavicon();
    this.setSVGIcons();
  }

  getAssetPath(assetName: string, defaultAsset = false) {
    return `${this.ROOT_PATH}/${
      defaultAsset
        ? this.DEFAULT_FOLDER
        : this.configService.getConfig().assetsFolder
    }/${assetName}`;
  }

  getSpecificAssetPath(assetPath: AssetPath, isMobile = false) {
    if (assetPath in this.configService.getConfig().assetsData.specifics) {
      return this.getAssetPath(
        this.configService.getConfig().assetsData.specifics[assetPath]
      );
    }

    if (isMobile) {
      this.getAssetPath(
        this.configService.getConfig().assetsData.defaultMobile
      );
    }

    return this.getAssetPath(this.configService.getConfig().assetsData.default);
  }

  getConfigLogoPath(mobile = false, withClientAppId: boolean) {
    return this.getAssetPath(
      `${this.configService.getConfig().id}${
        withClientAppId ? '_' + this.clientAppId : ''
      }_logo${mobile ? '_mobile' : ''}.svg`
    );
  }

  getSecondaryLogo(businessOrigin: string) {
    return this.getAssetPath(`business_origin_logos/${businessOrigin}.svg`);
  }

  getConfigInsurerLogoPath() {
    return this.getAssetPath(
      `${this.configService.getConfig().id}_insurer_logo.svg`
    );
  }

  getIconPath(iconName: IconName) {
    return this.getAssetPath(`icons.new/${iconName}.svg`, true);
  }

  getInsurerAssetPath(assetName: string, defaultAsset = false) {
    return this.getAssetPath(
      `${this.INUSRER_LOGO_PATH}/${assetName}`,
      defaultAsset
    );
  }

  getBrandLogoPath(brandLogo: string, defaultAsset = false) {
    return this.getAssetPath(`patterns/fitters/${brandLogo}`, defaultAsset);
  }

  private getFaviconPath() {
    return this.getAssetPath(
      `${this.configService.getConfig().id}_favicon.ico`
    );
  }

  private setFavicon() {
    const links = document.getElementsByTagName('link');
    for (const htmlElement of Array.from(links)) {
      if (htmlElement.getAttribute('rel') === 'icon') {
        htmlElement.setAttribute('href', this.getFaviconPath());
      }
    }
  }

  private setSVGIcons() {
    for (const iconName of ICON_LIST) {
      this.matIconRegistry.addSvgIcon(
        iconName,
        this.domSanitizer.bypassSecurityTrustResourceUrl(
          this.getIconPath(iconName)
        )
      );
    }
  }

  asBGImageURL(path: string) {
    return `url(${path})`;
  }

  getRoutingCustomer(): Observable<B2CRouting> {
    return this.routingCustomer.pipe(
      filter((routingCustomer) => !!routingCustomer),
      take(1)
    );
  }

  getImagesMapping(): Observable<IconsMap> {
    return this.iconsMap.pipe(
      filter((iconsMap) => !!iconsMap),
      take(1)
    );
  }

  getWeatherEvents(): Observable<WeatherEvent[]> {
    return this.weatherEvents.pipe(map((eventList) => eventList || []));
  }

  getLogoAlt(
    item: string,
    itemType:
      | 'fitterBrandIdLogo'
      | 'insurerIdLogo'
      | 'configIdLogo'
      | 'businessOriginCoLogo'
  ): Observable<string> {
    const itemWithClientAppId = `${item}_${this.clientAppId}`;

    return this.logoAlt.pipe(
      filter((logoAlt) => !!logoAlt),
      take(1),
      map(
        (logoAlt) =>
          logoAlt[itemType][itemWithClientAppId] ||
          logoAlt[itemType][item] ||
          ''
      )
    );
  }
}

interface LogoAlt {
  fitterBrandIdLogo: Record<string, string>;
  insurerIdLogo: Record<string, string>;
  configIdLogo: Record<string, string>;
  businessOriginCoLogo: Record<string, string>;
}
